/**
 * @module user.actions
 */
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { tokens } from '~/adapters';
import { graphqlRequest, hasGraphQLErrors, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import { host } from '~/config/settings/worktango.settings';
import { supportedLanguages, appName, defaultLang } from '~/config/settings/app.settings';
import { statuses as notificationStatuses } from '~/config/settings/notification.settings';
import { statuses as userStatuses, reverseServerStatuses } from '~/config/settings/user.settings';
import * as transformers from '../transformers';
import * as languages from '../languages';
import * as schemas from '../schemas';
import { appQueryClient } from '~/adapters/reactQuery';
import { accountKeys } from '../react-query/account';
import {
  DeletePendingUsersDocument,
  ExitImpersonationDocument,
  FetchAdminUsersDocument,
  FetchCompanyUserPermissionSummariesDocument,
  FetchCompanyUsersDocument,
  FetchUsersWithPermissionsDocument,
  ImpersonateUserDocument,
  UpdateUsersStatusDocument,
} from '~/gql/graphql';
/**
 * Fetch list of users for `companyId`.
 *
 * @param {number} options.companyId - database id for the company who's users are being fetched
 * @param {boolean} options.withPermissions - true: attach the summary permission shape
 *   (surveyTypeId + globalActionId) used by the UserList page; false: bare user fields.
 *   The deep permission shape for UserEdit goes through `fetchUsersWithPermissions` instead.
 * @returns {object} normalized user entities action
 */
const fetchUsers = createAsyncThunk(
  'user/fetchUsers',
  // @ts-expect-error TS(2339): Property 'companyId' does not exist on type 'void'... Remove this comment to see the full error message
  async ({ companyId, withPermissions = false }, { rejectWithValue }) => {
    const queryDoc = withPermissions ? FetchCompanyUserPermissionSummariesDocument : FetchCompanyUsersDocument;
    const res = await graphqlRequest(queryDoc, { companyId });

    if (res?.error) return rejectWithValue(res.error);

    const users = transformers.usersFrom(res.data?.getCompanyUsers);
    return normalize(users, [schemas.user]);
  },
);

/**
 * Fetch the deep permission shape for specific user ids only. Used by
 * UserEdit so the surrounding company-wide list can stay on the summary path.
 * Reducer merges (vs. replaces) so existing summary entities are preserved.
 *
 * @param {number[]} options.userIds - ids of the users to hydrate
 * @param {number} options.companyId - company database id
 */
const fetchUsersWithPermissions = createAsyncThunk(
  'user/fetchUsersWithPermissions',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds, companyId }, { rejectWithValue }) => {
    const res = await graphqlRequest(FetchUsersWithPermissionsDocument, { userIds, companyId });

    if (res?.error) return rejectWithValue(res.error);

    const users = transformers.usersFrom(res.data?.getUsersWithPermissions);
    return normalize(users, [schemas.user]);
  },
);

/**
 * Modify permissions for 1 or more users.
 *
 * @param {number[]} userIds - ids for the users that will be updated
 * @param {object} permissions - a set of user permissions that will be applied to all users
 * @returns {object} normalized action containing user entities
 */
const modifyUserPermissions = createAsyncThunk(
  'user/modifyUserPermissions',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds = [], permissions = {}, managerEmployeeIdAttrId = null }, { rejectWithValue, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const companyId = getState().account.companyId;
    const formattedPermissions = transformers.permissionsToIds(permissions, managerEmployeeIdAttrId);
    const url = `${host}/users/permissions`;

    const res = await httpRequest(url, {
      method: 'PUT',
      body: {
        userIds,
        companyId,
        permissions: formattedPermissions,
      },
    });

    if (res.error) {
      return rejectWithValue({
        title: languages.t('error.somethingWentWrong'),
        message: res.error.message || res.error.code,
        status: notificationStatuses.error,
      });
    }

    const users = transformers.usersFrom(res?.payload?.updatedPermissions);
    const normalizedData = normalize(users, [schemas.user]);

    return {
      ...normalizedData,
      // currently the backend is not returning full result, we are just refetch all users at this moment
      // so attach userIds here in order to unassign them from role in the users state
      userIds,
    };
  },
);

const changeProfile = createAsyncThunk(
  'user/change-profile',
  async (
    // @ts-expect-error TS(2339): Property 'userId' does not exist on type 'void'.
    { userId, lang, avatarBase64, avatarFile, firstName, lastName, password, currentPassword },
    { dispatch, rejectWithValue },
  ) => {
    if (lang) dispatch(changeLanguage(lang));

    if (avatarFile) {
      dispatch(
        // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
        changeAvatar({
          userId,
          file: avatarFile,
        }),
      );
    }

    const url = `${host}/users/${userId}/profile`;
    const res = await httpRequest(url, {
      method: 'POST',
      body: {
        lang,
        firstName,
        lastName,
        password,
        currentPassword,
      },
    });

    if (res.error) {
      return rejectWithValue(res.error);
    }

    const user = res.payload?.user;
    const token = res.payload?.token;

    tokens.saveAccessToken(token);

    appQueryClient.invalidateQueries(accountKeys.fetchCurrentAccount());

    return {
      userId: user.id,
      lang, // response does not include language ISO code, only database id
      langId: user.preferredLanguageId,
      firstName: user.firstName,
      lastName: user.lastName,
      avatarUrl: avatarBase64, // since avatar is a separate request, respond with base64 for immediate UI update
      notification: {
        title: languages.t('notifications.title.done'),
        message: languages.t('notifications.profile.updateSuccessful'),
        status: notificationStatuses.success,
      },
    };
  },
);

const changeLanguage = createAction('user/change-language', (lang) => {
  if (!supportedLanguages.includes(lang)) {
    return {
      payload: new Error(`Language not supported. Valid options are: ${supportedLanguages.join(',')}`),
      error: true,
    };
  }

  // Update state of i18n module (used by `useTranslate()` hook in React components).
  languages.changeLanguage(lang);

  return {
    payload: {
      lang,
    },
  };
});

// @ts-expect-error TS(2339): Property 'userId' does not exist on type 'void'.
const changeAvatar = createAsyncThunk('user/change-avatar', async ({ userId, file }) => {
  const url = `${host}/users/${userId}/avatar`;

  const formData = new FormData();
  formData.append('avatar', file);

  const res = await httpRequest(url, {
    method: 'POST',
    body: formData,
    skipContentType: true,
  });

  const avatarUrl = res?.payload?.avatar;

  return {
    avatarUrl,
  };
});

// @ts-expect-error TS(2339): Property 'roleId' does not exist on type 'void'.
const fetchRole = createAsyncThunk('user/fetchRole', async ({ roleId, companyId }, { rejectWithValue }) => {
  const url = `${host}/user-roles/${roleId}?companyId=${companyId}`;

  const res = await httpRequest(url);

  if (res?.error) return rejectWithValue(res.error);

  const role = transformers.roleFrom(res.payload.role);
  const normalizedData = normalize(role, schemas.role);

  return normalizedData;
});

// @ts-expect-error TS(2339): Property 'companyId' does not exist on type 'void'... Remove this comment to see the full error message
const fetchRoles = createAsyncThunk('user/fetchRoles', async ({ companyId }, { rejectWithValue }) => {
  const url = `${host}/user-roles?companyId=${companyId}`;

  const res = await httpRequest(url);
  if (res?.error) return rejectWithValue(res.error);

  const roles = transformers.rolesFrom(res.payload.roles);
  const normalizedData = normalize(roles, [schemas.role]);

  return normalizedData;
});

const createRole = createAsyncThunk(
  'user/createRole',
  // @ts-expect-error TS(2339): Property 'roleName' does not exist on type 'void'.
  async ({ roleName, permissions, managerEmployeeIdAttrId = null }, { rejectWithValue, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const account = getState().account;
    const formattedPermissions = transformers.permissionsToIds(permissions, managerEmployeeIdAttrId);

    const url = `${host}/user-roles`;

    const res = await httpRequest(url, {
      method: 'POST',
      body: {
        companyId: account.companyId,
        translations: { [account.lang]: { name: roleName } },
        permissions: formattedPermissions,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    const role = transformers.roleFrom(res.payload.role);
    const normalizedData = normalize([role], [schemas.role]);

    return { ...normalizedData };
  },
);

const updateRole = createAsyncThunk(
  'user/updateRole',
  // @ts-expect-error TS(2339): Property 'roleId' does not exist on type 'void'.
  async ({ roleId, roleName, permissions, userIds, managerEmployeeIdAttrId = null }, { rejectWithValue, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const account = getState().account;
    const formattedPermissions = permissions
      ? transformers.permissionsToIds(permissions, managerEmployeeIdAttrId)
      : null;
    const formattedTranslations = roleName ? { [account.lang]: { name: roleName } } : null;

    const url = `${host}/user-roles/${roleId}`;
    const res = await httpRequest(url, {
      method: 'PUT',
      body: {
        companyId: account.companyId,
        translations: formattedTranslations,
        permissions: formattedPermissions,
        userIds,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    const role = transformers.roleFrom(res.payload.role);
    const normalizedData = normalize([role], [schemas.role]);

    return { ...normalizedData };
  },
);

// @ts-expect-error TS(2339): Property 'id' does not exist on type 'void'.
const deleteRole = createAsyncThunk('user/deleteRole', async ({ id, name }, { rejectWithValue, getState }) => {
  // @ts-expect-error TS(2571): Object is of type 'unknown'.
  const account = getState().account;

  const url = `${host}/user-roles/${id}`;
  const res = await httpRequest(url, {
    method: 'DELETE',
    body: {
      companyId: account.companyId,
    },
  });

  if (res?.error) return rejectWithValue(res.error);

  return {
    id,
    notification: {
      title: languages.t('roles.delete.successTitle'),
      message: languages.t('roles.delete.successDesc', { name: name }),
      status: notificationStatuses.success,
    },
  };
});

const createUsers = createAsyncThunk(
  'user/createUsers',
  async (
    {
      // @ts-expect-error TS(2339): Property 'lang' does not exist on type 'void'.
      lang = defaultLang,
      // @ts-expect-error TS(2339): Property 'from' does not exist on type 'void'.
      from = appName,
      // @ts-expect-error TS(2339): Property 'emails' does not exist on type 'void'.
      emails = [],
      // @ts-expect-error TS(2339): Property 'permissions' does not exist on type 'voi... Remove this comment to see the full error message
      permissions = null,
      // @ts-expect-error TS(2339): Property 'userRoleId' does not exist on type 'void... Remove this comment to see the full error message
      userRoleId = null,
      // @ts-expect-error TS(2339): Property 'managerEmployeeIdAttrId' does not exist ... Remove this comment to see the full error message
      managerEmployeeIdAttrId = null,
    },
    { rejectWithValue, getState },
  ) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const companyId = getState().account.companyId;
    const formattedPermissions = permissions && transformers.permissionsToIds(permissions, managerEmployeeIdAttrId);
    const url = `${host}/users`;

    const res = await httpRequest(url, {
      method: 'POST',
      body: {
        companyId,
        lang,
        from,
        emails,
        permissions: userRoleId ? null : formattedPermissions,
        userRoleId,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    const users = transformers.usersFrom(res?.payload?.updatedPermissions || res?.payload?.users);
    return normalize(users, [schemas.user]);
  },
);

const resendUserInvites = createAsyncThunk(
  'user/resendUserInvites',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds = [] }, { rejectWithValue, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const account = getState().account;
    const url = `${host}/users/resend`;

    const res = await httpRequest(url, {
      method: 'POST',
      body: {
        companyId: account.companyId,
        userIds,
        from: account.name,
        lang: account.lang,
      },
    });

    if (res?.error) return rejectWithValue(res.error);

    return {
      notification: {
        title: languages.t('notifications.title.done'),
        message: 'Invitations re-sent successfully.',
        status: notificationStatuses.success,
      },
    };
  },
);

const deletePendingUsers = createAsyncThunk(
  'user/deleteUsers',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds = [] }, { rejectWithValue, dispatch, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const companyId = getState().account.companyId;
    const res = await graphqlRequest(DeletePendingUsersDocument, {
      ids: userIds,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
    dispatch(fetchUsers({ companyId, withPermissions: true }));

    return {
      notification: {
        title: languages.t('notifications.title.done'),
        message: `${userIds.length} pending user${userIds.length > 1 ? 's' : ''} successfully deleted.`,
        status: notificationStatuses.success,
      },
    };
  },
);

const activateUsers = createAsyncThunk(
  'user/activateUsers',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds = [] }, { rejectWithValue, dispatch, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const companyId = getState().account.companyId;
    const activeStatus = reverseServerStatuses[userStatuses.active];
    const res = await graphqlRequest(UpdateUsersStatusDocument, {
      ids: userIds,
      status: activeStatus,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
    dispatch(fetchUsers({ companyId, withPermissions: true }));

    return {
      notification: {
        title: languages.t('notifications.title.done'),
        message: `${userIds.length} user${userIds.length > 1 ? 's' : ''} successfully activated.`,
        status: notificationStatuses.success,
      },
    };
  },
);

const deactivateUsers = createAsyncThunk(
  'user/deactivateUsers',
  // @ts-expect-error TS(2339): Property 'userIds' does not exist on type 'void'.
  async ({ userIds = [] }, { rejectWithValue, dispatch, getState }) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const companyId = getState().account.companyId;
    const deactivatedStatus = reverseServerStatuses[userStatuses.deactivated];
    const res = await graphqlRequest(UpdateUsersStatusDocument, {
      ids: userIds,
      status: deactivatedStatus,
    });

    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

    // @ts-expect-error TS(2554): Expected 0 arguments, but got 1.
    dispatch(fetchUsers({ companyId, withPermissions: true }));

    return {
      notification: {
        title: languages.t('notifications.title.done'),
        message: `${userIds.length} user${userIds.length > 1 ? 's' : ''} successfully deactivated.`,
        status: notificationStatuses.success,
      },
    };
  },
);

// TODO: Handle users export through action creator rather than through legacy context.
const exportExcelUsers = createAsyncThunk('user/exportExcelUsers', async () => {
  //   const users = getState().entities.users;
  //   const TAB_PAGES = [
  //     {
  //       id: 'active',
  //       label: 'system.field.enabled',
  //       index: 0
  //     },
  //     {
  //       id: 'pending',
  //       label: 'pending',
  //       index: 1
  //     },
  //     {
  //       id: 'disabled',
  //       label: 'survey.type.disabled',
  //       index: 2
  //     }
  //   ];
  //   const userFields = [
  //     { label: languages.t('admin.users.email'), value: 'email', allowSort: true },
  //     { label: languages.t('admin.users.name'), value: 'name', allowSort: true },
  //     { label: languages.t('admin.users.permissions'), value: 'globalActions', allowSort: true },
  //     { label: languages.t('surveyTypes'), value: 'types', allowSort: true }
  //   ];
  //   const usersData = users.map(user => ({
  //     [languages.t('admin.users.name')]: user.name,
  //     [languages.t('admin.users.email')]: user.email,
  //     [languages.t('selected.survey.types.header')]: user.types,
  //     [languages.t('admin.users.permissions')]: user.globalActions
  //   }));
  //   // const matchedStatus = TAB_PAGES.find(page => page.index === statusIndex);
  //   const params = {
  //     // tabName: languages.t(matchedStatus.label)
  //   };
  //     // excelExport(usersData, userFields, EXPORT_USERS_CSV, params);
  //   const file = excel.toFile('Users', usersData, userFields, params);
});

const fetchAdminUsers = createAsyncThunk('user/fetchAdminUsers', async (_, { rejectWithValue }) => {
  const res = await graphqlRequest(FetchAdminUsersDocument);

  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  if (hasGraphQLErrors(res)) return rejectWithValue(res.errors[0]);

  const users = transformers.usersFrom(res?.data?.adminUsers);
  const normalizedData = normalize(users, [schemas.user]);

  return normalizedData;
});

const impersonateUser = createAsyncThunk(
  'user/impersonateUser',
  // @ts-expect-error TS(2339): Property 'userId' does not exist on type 'void'.
  async ({ userId }, { rejectWithValue }) => {
    try {
      const res = await graphqlRequest(ImpersonateUserDocument, { userId });

      const token = res?.data?.impersonateUser?.token;
      if (!token) {
        return rejectWithValue({ message: 'No token returned from impersonateUser' });
      }

      tokens.saveAccessToken(token);
      window.location.assign('/');
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const exitImpersonation = createAsyncThunk('user/exitImpersonation', async (_, { rejectWithValue }) => {
  try {
    const res = await graphqlRequest(ExitImpersonationDocument);

    const token = res?.data?.exitImpersonation?.token;
    if (!token) {
      return rejectWithValue({ message: 'No token returned from exitImpersonation' });
    }

    tokens.saveAccessToken(token);
    window.location.assign('/');
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const userActions = {
  changeProfile,
  changeLanguage,
  changeAvatar,
  createRole,
  deleteRole,
  modifyUserPermissions,
  resendUserInvites,
  fetchRole,
  fetchRoles,
  fetchUsers,
  fetchUsersWithPermissions,
  createUsers,
  deletePendingUsers,
  activateUsers,
  deactivateUsers,
  exportExcelUsers,
  updateRole,
  fetchAdminUsers,
  impersonateUser,
  exitImpersonation,
};
